import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from "react";
import { Notification, NotificationGroup } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";

const ToastrContext = createContext(null);

export function ToastrProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const idRef = useRef(0);

  // Stable function reference — keeps context consumers from re-rendering on
  // every toast add/remove, which would otherwise cascade through the app
  // and disrupt active inline-edit cells.
  const showToastr = useCallback((type, message) => {
    const id = ++idRef.current;
    setNotifications(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 10000);
  }, []);

  // Stable context value — same object reference across renders.
  const contextValue = useMemo(() => ({ showToastr }), [showToastr]);

  return (
    <ToastrContext.Provider value={contextValue}>
      {children}
      <NotificationGroup
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}
      >
        {notifications.map(n => (
          <Fade key={n.id} enter exit>
            <Notification
              type={{ style: n.type, icon: true }}
              closable={true}
              onClose={() => setNotifications(prev => prev.filter(x => x.id !== n.id))}
            >
              <span>{n.message}</span>
            </Notification>
          </Fade>
        ))}
      </NotificationGroup>
    </ToastrContext.Provider>
  );
}

export function useToastr() {
  const ctx = useContext(ToastrContext);
  if (!ctx) throw new Error("useToastr must be used within a ToastrProvider");
  return ctx;
}
